import { create } from 'zustand';
import type { BuilderState, DesignState, ProductType, ProductSurface, ProductConfig, AvailableColor, AvailableSize } from '../types/builder.types';
import type { DesignNode } from '../types/node.types';
import type { ValidationResult } from '../lib/validation/types';
import type { PricingConfig } from '@printtat/shared';

interface VariantInput {
  size: string;
  color: string;
  colorHex: string;
  outlineHex: string;
  additionalPrice: number;
  stockCount: number;
  isActive: boolean;
  isDefault: boolean;
}

function deriveVariantState(variants: VariantInput[] = []) {
  const colorMap = new Map<string, { hex: string; outlineHex: string; totalStock: number }>();
  const sizeMap = new Map<string, { additionalPrice: number; totalStock: number }>();

  for (const v of variants) {
    if (!v.isActive) continue;
    const existing = colorMap.get(v.color);
    if (existing) {
      existing.totalStock += v.stockCount;
    } else {
      colorMap.set(v.color, { hex: v.colorHex, outlineHex: v.outlineHex, totalStock: v.stockCount });
    }
    const existingSize = sizeMap.get(v.size);
    if (existingSize) {
      existingSize.totalStock += v.stockCount;
    } else {
      sizeMap.set(v.size, { additionalPrice: v.additionalPrice, totalStock: v.stockCount });
    }
  }

  const availableColors: AvailableColor[] = Array.from(colorMap.entries()).map(([name, data]) => ({
    name,
    hex: data.hex,
    outlineHex: data.outlineHex,
    hasStock: data.totalStock > 0,
  }));

  const availableSizes: AvailableSize[] = Array.from(sizeMap.entries()).map(([name, data]) => ({
    name,
    additionalPrice: data.additionalPrice,
    hasStock: data.totalStock > 0,
  }));

  const enabledColors = availableColors.filter((c) => c.hasStock).map((c) => c.name);
  const enabledSizes = availableSizes.filter((s) => s.hasStock).map((s) => s.name);

  // Admin flags one active variant per product as the default via product_variants.is_default.
  // Its color/size drive the initial selection for new designs. Fall back to
  // the first enabled entry when nothing is flagged.
  const flagged = variants.find((v) => v.isDefault && v.isActive);
  const defaultColor = flagged && enabledColors.includes(flagged.color)
    ? flagged.color
    : enabledColors[0] ?? null;
  const defaultSize = flagged && enabledSizes.includes(flagged.size)
    ? flagged.size
    : enabledSizes[0] ?? null;
  const selectedColor = availableColors.find((c) => c.name === defaultColor) ?? null;

  return { availableColors, availableSizes, enabledColors, enabledSizes, defaultColor, defaultSize, selectedColor };
}

function initSurfaceOptionDefaults(config: PricingConfig | null): Record<string, Record<string, string>> {
  if (!config?.surfaceOptions) return {};
  const selections: Record<string, Record<string, string>> = {};
  for (const [surface, defs] of Object.entries(config.surfaceOptions)) {
    selections[surface] = {};
    for (const def of defs) {
      selections[surface][def.key] = def.default;
    }
  }
  return selections;
}

// Minimal initial state — the real config is loaded via initFromProduct/initFromDesign
const EMPTY_CONFIG: ProductConfig = {
  type: 't-shirt' as ProductType,
  surfaces: [] as ProductSurface[],
  dimensions: {},
  canvasOffset: {},
  canvasWidthRatio: {},
  safeAreas: {},
};

const initialDesignState: DesignState = {
  surfaces: {},
  selectedNodeId: null
};

export const useBuilderStore = create<BuilderState>((set, get) => ({
  currentProduct: 't-shirt',
  currentSurface: 'front',
  selectedNodeId: null,
  productConfig: EMPTY_CONFIG,
  surfaces: initialDesignState.surfaces,
  importVersion: 0,
  historyVersion: 0,
  designName: 'Untitled design',
  designDescription: '',
  designId: null,
  productId: null,
  isDirty: false,
  pricingConfig: null,
  surfaceOptionSelections: {},
  basePrice: 0,
  nodeValidation: {},
  availableColors: [],
  selectedColor: null,
  enabledColors: [],
  defaultColor: null,
  availableSizes: [],
  enabledSizes: [],
  defaultSize: null,
  history: {
    past: [],
    present: initialDesignState,
    future: []
  },

  setCurrentProduct: (product: ProductType) => {
    set({ currentProduct: product });
  },

  setCurrentSurface: (surface: ProductSurface) => {
    set({ currentSurface: surface });
  },

  setSelectedNodeId: (nodeId: string | null) => {
    set({ selectedNodeId: nodeId });
  },

  selectNode: (nodeId: string | null) => {
    set({ selectedNodeId: nodeId });
  },

  addNode: (node: DesignNode) => {
    const state = get();
    const newSurfaces = {
      ...state.surfaces,
      [node.surface]: [...state.surfaces[node.surface], node]
    };
    
    const newDesignState: DesignState = {
      surfaces: newSurfaces,
      selectedNodeId: state.selectedNodeId // Keep current selection, don't auto-select new node
    };

    set({
      surfaces: newSurfaces,
      selectedNodeId: state.selectedNodeId, // Keep current selection
      isDirty: true,
      history: {
        past: [...state.history.past, state.history.present],
        present: newDesignState,
        future: []
      }
    });
  },

  clearCurrentSurface: () => {
    const state = get();
    const surface = state.currentSurface;
    if (!state.surfaces[surface] || state.surfaces[surface].length === 0) return;

    const newSurfaces = {
      ...state.surfaces,
      [surface]: [],
    };
    const newDesignState: DesignState = {
      surfaces: newSurfaces,
      selectedNodeId: null,
    };

    set({
      surfaces: newSurfaces,
      selectedNodeId: null,
      isDirty: true,
      importVersion: state.importVersion + 1,
      history: {
        past: [...state.history.past, state.history.present],
        present: newDesignState,
        future: [],
      },
    });
  },

  updateNode: (id: string, updates: Partial<DesignNode>) => {
    console.log('Store updateNode called with id:', id, 'updates:', updates);
    const state = get();
    const newSurfaces = { ...state.surfaces };

    Object.keys(newSurfaces).forEach(surface => {
      newSurfaces[surface] = newSurfaces[surface].map(node =>
        node.id === id ? { ...node, ...updates } : node
      );
    });

    const newDesignState: DesignState = {
      surfaces: newSurfaces,
      selectedNodeId: state.selectedNodeId
    };

    console.log('Store updating surfaces:', newSurfaces);

    set({
      surfaces: newSurfaces,
      isDirty: true,
      history: {
        past: [...state.history.past, state.history.present],
        present: newDesignState,
        future: []
      }
    });
  },

  // Same as updateNode but does not mark dirty or push to undo history.
  // Used for canvas-internal corrections (e.g. text width re-measurement on load).
  updateNodeSilent: (id: string, updates: Partial<DesignNode>) => {
    const state = get();
    const newSurfaces = { ...state.surfaces };

    Object.keys(newSurfaces).forEach(surface => {
      newSurfaces[surface] = newSurfaces[surface].map(node =>
        node.id === id ? { ...node, ...updates } : node
      );
    });

    set({
      surfaces: newSurfaces,
      history: {
        ...state.history,
        present: { surfaces: newSurfaces, selectedNodeId: state.selectedNodeId },
      },
    });
  },

  deleteNode: (id: string) => {
    const state = get();
    const newSurfaces = { ...state.surfaces };

    Object.keys(newSurfaces).forEach(surface => {
      newSurfaces[surface] = newSurfaces[surface].filter(node => node.id !== id);
    });

    const newDesignState: DesignState = {
      surfaces: newSurfaces,
      selectedNodeId: state.selectedNodeId === id ? null : state.selectedNodeId
    };

    set({
      surfaces: newSurfaces,
      selectedNodeId: state.selectedNodeId === id ? null : state.selectedNodeId,
      isDirty: true,
      history: {
        past: [...state.history.past, state.history.present],
        present: newDesignState,
        future: []
      }
    });
  },

  undo: () => {
    const state = get();
    if (state.history.past.length === 0) return;

    const previous = state.history.past[state.history.past.length - 1];
    const newPast = state.history.past.slice(0, -1);

    set({
      surfaces: previous.surfaces,
      selectedNodeId: previous.selectedNodeId,
      isDirty: true,
      historyVersion: state.historyVersion + 1,
      history: {
        past: newPast,
        present: previous,
        future: [state.history.present, ...state.history.future]
      }
    });
  },

  redo: () => {
    const state = get();
    if (state.history.future.length === 0) return;

    const next = state.history.future[0];
    const newFuture = state.history.future.slice(1);

    set({
      surfaces: next.surfaces,
      selectedNodeId: next.selectedNodeId,
      isDirty: true,
      historyVersion: state.historyVersion + 1,
      history: {
        past: [...state.history.past, state.history.present],
        present: next,
        future: newFuture
      }
    });
  },

  exportDesign: () => {
    const state = get();
    return JSON.stringify({
      designName: state.designName,
      designDescription: state.designDescription,
      surfaceOptions: state.surfaceOptionSelections,
      enabledColors: state.enabledColors,
      enabledSizes: state.enabledSizes,
      defaultColor: state.defaultColor,
      defaultSize: state.defaultSize,
      ...state.history.present,
    });
  },

  setDesignName: (name: string) => set({ designName: name, isDirty: true }),

  setDesignDescription: (description: string) => set({ designDescription: description, isDirty: true }),

  setNodeValidation: (nodeId: string, results: ValidationResult[]) =>
    set(state => ({ nodeValidation: { ...state.nodeValidation, [nodeId]: results } })),

  clearNodeValidation: (nodeId: string) =>
    set(state => {
      const rest = Object.fromEntries(
        Object.entries(state.nodeValidation).filter(([key]) => key !== nodeId)
      );
      return { nodeValidation: rest };
    }),

  setSelectedColor: (color: AvailableColor) => set({ selectedColor: color }),

  toggleColor: (colorName: string) => {
    set((state) => {
      const isEnabled = state.enabledColors.includes(colorName);
      const enabledColors = isEnabled
        ? state.enabledColors.filter((c) => c !== colorName)
        : [...state.enabledColors, colorName];
      // If we disabled the default, pick next enabled
      const defaultColor =
        state.defaultColor === colorName && isEnabled
          ? enabledColors[0] ?? null
          : state.defaultColor;
      const selectedColor =
        state.selectedColor?.name === colorName && isEnabled
          ? (state.availableColors.find((c) => c.name === enabledColors[0]) ?? null)
          : state.selectedColor;
      return { enabledColors, defaultColor, selectedColor };
    });
  },

  setDefaultColor: (colorName: string) => set({ defaultColor: colorName }),

  toggleSize: (sizeName: string) => {
    set((state) => {
      const isEnabled = state.enabledSizes.includes(sizeName);
      const enabledSizes = isEnabled
        ? state.enabledSizes.filter((s) => s !== sizeName)
        : [...state.enabledSizes, sizeName];
      const defaultSize =
        state.defaultSize === sizeName && isEnabled
          ? enabledSizes[0] ?? null
          : state.defaultSize;
      return { enabledSizes, defaultSize };
    });
  },

  setDefaultSize: (sizeName: string) => set({ defaultSize: sizeName }),

  setSurfaceOption: (surface: string, key: string, value: string) => {
    set(state => ({
      surfaceOptionSelections: {
        ...state.surfaceOptionSelections,
        [surface]: {
          ...(state.surfaceOptionSelections[surface] || {}),
          [key]: value,
        },
      },
      isDirty: true,
    }));
  },

  importDesign: (jsonString: string) => {
    try {
      const parsed = JSON.parse(jsonString);
      const { designName, designDescription, ...designState } = parsed as DesignState & {
        designName?: string;
        designDescription?: string;
      };
      set((state) => ({
        surfaces: designState.surfaces,
        selectedNodeId: designState.selectedNodeId,
        designName: designName ?? 'Untitled design',
        designDescription: designDescription ?? '',
        isDirty: true,
        importVersion: state.importVersion + 1,
        history: {
          past: [],
          present: designState,
          future: []
        }
      }));
    } catch (error) {
      console.error('Failed to import design:', error);
    }
  },

  initFromProduct: (product) => {
    const config: ProductConfig = {
      type: product.category as ProductType,
      surfaces: product.printAreas.surfaces,
      dimensions: product.printAreas.dimensions,
      canvasOffset: product.printAreas.canvasOffset ?? {},
      canvasWidthRatio: product.printAreas.canvasWidthRatio ?? {},
      safeAreas: product.printAreas.safeAreas,
      mockups: product.printAreas.mockups,
      surfaceLabels: product.printAreas.surfaceLabels,
    };
    const pricingConfig = (product.pricingConfig ?? null) as PricingConfig | null;
    const emptySurfaces: Record<string, DesignNode[]> = {};
    config.surfaces.forEach((s: string) => { emptySurfaces[s] = []; });
    const newDesignState: DesignState = { surfaces: emptySurfaces, selectedNodeId: null };
    const variantState = deriveVariantState(product.variants);

    set((state) => ({
      currentProduct: config.type,
      currentSurface: config.surfaces[0] || 'front',
      selectedNodeId: null,
      productConfig: config,
      surfaces: emptySurfaces,
      designId: null,
      productId: product.id,
      designName: 'Untitled design',
      designDescription: '',
      isDirty: false,
      pricingConfig,
      basePrice: product.basePrice ?? 0,
      surfaceOptionSelections: initSurfaceOptionDefaults(pricingConfig),
      ...variantState,
      importVersion: state.importVersion + 1,
      nodeValidation: {},
      history: { past: [], present: newDesignState, future: [] },
    }));
  },

  initFromDesign: (design) => {
    const config: ProductConfig = {
      type: design.product.category as ProductType,
      surfaces: design.product.printAreas.surfaces,
      dimensions: design.product.printAreas.dimensions,
      canvasOffset: design.product.printAreas.canvasOffset ?? {},
      canvasWidthRatio: design.product.printAreas.canvasWidthRatio ?? {},
      safeAreas: design.product.printAreas.safeAreas,
      mockups: design.product.printAreas.mockups,
      surfaceLabels: design.product.printAreas.surfaceLabels,
    };
    const pricingConfig = (design.product.pricingConfig ?? null) as PricingConfig | null;
    const canvasData = design.canvasData as { surfaces?: Record<string, DesignNode[]>; surfaceOptions?: Record<string, Record<string, string>>; enabledColors?: string[]; enabledSizes?: string[]; defaultColor?: string | null; defaultSize?: string | null };
    const loadedSurfaces: Record<string, DesignNode[]> = {};
    config.surfaces.forEach((s: string) => { loadedSurfaces[s] = canvasData.surfaces?.[s] || []; });
    const newDesignState: DesignState = { surfaces: loadedSurfaces, selectedNodeId: null };

    // Restore saved surface options, falling back to defaults
    const defaults = initSurfaceOptionDefaults(pricingConfig);
    const savedOptions = canvasData.surfaceOptions ?? {};
    const mergedOptions: Record<string, Record<string, string>> = {};
    for (const surface of Object.keys(defaults)) {
      mergedOptions[surface] = { ...defaults[surface], ...savedOptions[surface] };
    }

    const variantState = deriveVariantState(design.product.variants);

    // Restore saved variant selections if present in canvasData
    const savedEnabledColors = canvasData.enabledColors as string[] | undefined;
    const savedEnabledSizes = canvasData.enabledSizes as string[] | undefined;
    const savedDefaultColor = canvasData.defaultColor as string | null | undefined;
    const savedDefaultSize = canvasData.defaultSize as string | null | undefined;

    if (savedEnabledColors) {
      // Only keep colors that still exist in the product's available colors
      const validColorNames = new Set(variantState.availableColors.map(c => c.name));
      variantState.enabledColors = savedEnabledColors.filter(c => validColorNames.has(c));
    }
    if (savedEnabledSizes) {
      const validSizeNames = new Set(variantState.availableSizes.map(s => s.name));
      variantState.enabledSizes = savedEnabledSizes.filter(s => validSizeNames.has(s));
    }
    if (savedDefaultColor !== undefined) {
      // Ensure default color is still in enabledColors
      variantState.defaultColor = savedDefaultColor && variantState.enabledColors.includes(savedDefaultColor)
        ? savedDefaultColor
        : variantState.enabledColors[0] ?? null;
    }
    if (savedDefaultSize !== undefined) {
      variantState.defaultSize = savedDefaultSize && variantState.enabledSizes.includes(savedDefaultSize)
        ? savedDefaultSize
        : variantState.enabledSizes[0] ?? null;
    }
    // Derive selectedColor from the resolved defaultColor
    variantState.selectedColor = variantState.availableColors.find(
      c => c.name === variantState.defaultColor
    ) ?? null;

    set((state) => ({
      currentProduct: config.type,
      currentSurface: config.surfaces[0] || 'front',
      selectedNodeId: null,
      productConfig: config,
      surfaces: loadedSurfaces,
      designId: design.id,
      productId: design.product.id,
      designName: design.name,
      designDescription: design.description || '',
      isDirty: false,
      pricingConfig,
      basePrice: design.product.basePrice ?? 0,
      surfaceOptionSelections: mergedOptions,
      ...variantState,
      importVersion: state.importVersion + 1,
      nodeValidation: {},
      history: { past: [], present: newDesignState, future: [] },
    }));
  },

  markSaved: (designId: string) => {
    set({ designId, isDirty: false });
  },
}));
